import React, { ReactNode } from "react";
import { Link } from "react-router-dom";
import { IconContext } from "react-icons";
import { AiOutlineSafetyCertificate } from "react-icons/ai";
import { MdOutlineAutoAwesome } from "react-icons/md";
import { PiLightningLight } from "react-icons/pi";
import { RiMedalLine } from "react-icons/ri";
import { BsClipboardData } from "react-icons/bs";
import { GoLinkExternal } from "react-icons/go";

type Feature = {
  title: string;
  description: string;
  icon: ReactNode;
};

type Service = {
  title: string;
  description: string;
  href: string;
  icon: ReactNode;
};

type FooterLink = {
  name: string;
  href: string;
  external?: boolean;
};

type AppsLandingProps = {
  contactHref: string;
  children?: ReactNode;
};

const iconStyle = { color: "#232", size: "24px" };

const features: Feature[] = [
  {
    title: "安全",
    description: "安心してデータを預けることができる",
    icon: (
      <IconContext.Provider value={iconStyle}>
        <AiOutlineSafetyCertificate />
      </IconContext.Provider>
    ),
  },
  {
    title: "速さ",
    description: "効率的でストレスなく利用できる",
    icon: (
      <IconContext.Provider value={iconStyle}>
        <PiLightningLight />
      </IconContext.Provider>
    ),
  },
  {
    title: "使いやすさ",
    description: "簡単に誰でもすぐに使える",
    icon: (
      <IconContext.Provider value={iconStyle}>
        <MdOutlineAutoAwesome />
      </IconContext.Provider>
    ),
  },
  {
    title: "安定",
    description: "機能の更新が少なく長く使える",
    icon: (
      <IconContext.Provider value={iconStyle}>
        <RiMedalLine />
      </IconContext.Provider>
    ),
  },
];

const services: Service[] = [
  {
    title: "不動産投資シミュレーター",
    description:
      "キャッシュフローやIRRを自動的に計算し、不動産投資の収益性をシミュレーションできます。",
    href: "https://realty.femto.work/",
    icon: (
      <IconContext.Provider value={iconStyle}>
        <BsClipboardData />
      </IconContext.Provider>
    ),
  },
];

const footerLinks: FooterLink[] = [
  { name: "Values", href: "#values" },
  { name: "Services", href: "#services" },
  { name: "お問い合わせ", href: "" },
];

const companyLinks: FooterLink[] = [
  { name: "コーポレートサイト", href: "/" },
  { name: "Privacy Policy", href: "/privacy" },
];

const renderFooterLink = (link: FooterLink) => {
  if (link.external || link.href.includes("#")) {
    return (
      <a
        href={link.href}
        className="text-sm leading-6 text-gray-600 hover:text-gray-900"
        rel={link.external ? "noopener noreferrer" : undefined}
        target={link.external ? "_blank" : undefined}
      >
        {link.name}
      </a>
    );
  }

  if (link.href.startsWith("/")) {
    return (
      <Link className="text-sm leading-6 text-gray-600 hover:text-gray-900" to={link.href}>
        {link.name}
      </Link>
    );
  }

  return (
    <a className="text-sm leading-6 text-gray-600 hover:text-gray-900" href={link.href}>
      {link.name}
    </a>
  );
};

const AppsLanding: React.FC<AppsLandingProps> = ({ children, contactHref }) => {
  const pageLinks = footerLinks.map((link) =>
    link.name === "お問い合わせ" ? { ...link, href: contactHref } : link
  );

  return (
    <>
      <header className="absolute inset-x-0 top-0 z-50">
        <nav
          aria-label="Global"
          className="mx-auto flex max-w-7xl items-center justify-between px-6 py-6 lg:px-8"
        >
          <div className="flex flex-1">
            <Link className="-m-1.5 p-1.5" to="/">
              <span className="sr-only">Femtowork Inc.</span>
              <img className="h-8 w-auto" src="/images/femtowork-logo.png" alt="Femtowork Inc." />
            </Link>
          </div>
          <div className="hidden md:flex md:items-center md:gap-8">
            <a className="text-sm font-semibold text-gray-900" href="#values">
              Values
            </a>
            <a className="text-sm font-semibold text-gray-900" href="#services">
              Services
            </a>
            <a className="text-sm font-semibold text-gray-900" href={contactHref}>
              Contact
            </a>
          </div>
        </nav>
      </header>

      <main className="isolate">
        <section className="relative pt-14">
          <div className="absolute inset-0">
            <img
              alt="Femto Apps cover"
              className="h-full w-full object-cover opacity-80"
              src="/images/apps-cover.jpeg"
            />
            <div className="absolute inset-0 bg-white opacity-60" />
          </div>
          <div className="relative py-24 sm:py-32">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
              <div className="mx-auto max-w-3xl text-center">
                <h1 className="text-4xl font-bold leading-snug tracking-tight text-gray-900 sm:text-6xl sm:leading-tight">
                  シンプルさが創造性を高める
                </h1>
                <p className="mt-6 text-lg leading-8 text-gray-600">
                  世界の企業の99%は小さな組織です。FemtoworkはAI時代のスモールビジネスや個人の創造性と生産性を高めるサービスを提供します。
                </p>
                <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                  <a
                    className="rounded-full bg-indigo-600 px-6 py-3 text-sm font-semibold text-white hover:bg-indigo-700"
                    href="#services"
                  >
                    サービスを見る
                  </a>
                  <a
                    className="rounded-full bg-white px-6 py-3 text-sm font-semibold text-gray-900 hover:bg-gray-100"
                    href={contactHref}
                  >
                    相談する
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-7xl px-6 sm:mt-20 lg:px-8" id="values">
          <div className="mx-auto max-w-3xl text-center lg:text-center">
            <p className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Values</p>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              私たちはシンプルさの力を信じています。ストレスのない利用体験のために、品質を以下のように定めています。
            </p>
          </div>
          <div className="mx-auto mt-16 sm:mt-20">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              {features.map((feature) => (
                <div key={feature.title} className="rounded-3xl border border-gray-200 bg-white p-8">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-100">
                    {feature.icon}
                  </div>
                  <h2 className="mt-6 text-xl font-semibold text-gray-900">{feature.title}</h2>
                  <p className="mt-3 text-base leading-7 text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto mt-32 max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Femto Appsであなたのビジネスをデザインする
            </h2>
            <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-gray-600">
              ソフトウェアは複雑になり、ユーザーが自分のビジネスを自分でデザインすることが難しくなっています。
            </p>
            <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-gray-600">
              私たちは、お客様が自由に自分のビジネスにあわせて利用できるシンプルなソフトウェアを提供し、
              ビジネスの成功に集中できるように支援します。
            </p>
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-7xl px-6 sm:mt-20 lg:px-8" id="services">
          <div className="mx-auto max-w-3xl text-center lg:text-center">
            <p className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Services</p>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              仕事や生活で使えるツールを提供しています。現在は、個人や小規模チーム向けの実用的なサービスを公開しています。
            </p>
          </div>

          <div className="mx-auto mt-16 max-w-5xl sm:mt-20">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {services.map((service) => (
                <article
                  key={service.title}
                  className="flex h-full flex-col rounded-3xl border border-gray-200 bg-white p-8"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-100">
                    {service.icon}
                  </div>
                  <h2 className="mt-6 text-xl font-semibold text-gray-900">{service.title}</h2>
                  <p className="mt-3 flex-1 text-base leading-7 text-gray-600">{service.description}</p>
                  <a
                    className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-indigo-600"
                    href={service.href}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    詳しく見る
                    <GoLinkExternal />
                  </a>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      {children}

      <div className="mx-auto mt-24 max-w-7xl px-6 lg:px-8">
        <footer className="border-t border-gray-200 py-16">
          <div className="xl:grid xl:grid-cols-3 xl:gap-8">
            <div>
              <Link className="inline-block" to="/">
                <img className="h-7 w-auto" src="/images/femtowork-logo.png" alt="Femtowork Inc." />
              </Link>
              <p className="mt-4 max-w-xs text-sm leading-6 text-gray-600">
                Small teams and independent builders のために、長く使えるシンプルなソフトウェアを提供します。
              </p>
            </div>
            <div className="mt-16 grid grid-cols-2 gap-8 xl:col-span-2 xl:mt-0">
              <div>
                <h3 className="text-sm font-semibold leading-6 text-gray-900">Navigate</h3>
                <ul className="mt-6 space-y-4">
                  {pageLinks.map((item) => (
                    <li key={item.name}>{renderFooterLink(item)}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold leading-6 text-gray-900">Company</h3>
                <ul className="mt-6 space-y-4">
                  {companyLinks.map((item) => (
                    <li key={item.name}>{renderFooterLink(item)}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default AppsLanding;
